import { lazy } from 'react';
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';

import { createTenantApp } from '@/app/create-tenant-app';
import { starmagazineHomeLoader } from './home/home.loader';
import { homeSearchSchema } from './home/home.schema';

// Lazy loaded routes
const LazyHome = lazy(() => import('./home/home'));
// Through the router (`lazyRouteComponent`), not `React.lazy`: the router
// preloads this chunk as part of the navigation, so a cover's view transition
// takes its "after" snapshot of the loaded preview. With `React.lazy` the
// first visit snapshotted an empty page while the chunk loaded, and the cover
// had nothing to morph into (every time in Safari).
const LazyPreview = lazyRouteComponent(
	() => import('@/templates/starmagazine/preview/preview'),
);
// The same, so home's More morphs its row's covers into the section's grid.
const LazySection = lazyRouteComponent(() => import('./section/section'));
const LazyGames = lazy(() => import('./games/games'));

export const router = createTenantApp({
	home: {
		path: '/',
		component: LazyHome,
		loader: starmagazineHomeLoader,
		validateSearch: homeSearchSchema,
	},
	// starmagazine's content is free (the tenant's decision, 2026-10-14): no
	// sign-in is required to read, so the issue routes declare no `protect`
	// and there is no subscription flow, the same as toyota.
	routes: [
		'issue',
		'issueArticle',
		// `/article/$id`: the home's feed and games rows open their article
		// on its own here, not inside its issue. Ungated, like the issues.
		'article',
		'drafts',
		'callback',
		'logout',
		// The offline reader behind the AppMenu's Downloads entry
		// (`widgets/downloads-drawer`); ungated, like the issue routes.
		'downloadedIssue',
		'downloadedIssueArticle',
	],
	auth: {
		// The wrapper this replaces never called `useAuth`, so the shared
		// issue route's default token-auth gate is explicitly opted out of
		// rather than newly introduced here.
		useRouteGate: () => undefined,
	},
	// sections/$id, games, and preview (its own @/templates/starmagazine/
	// preview/Preview, not the shared catalog entry) are starmagazine-
	// exclusive. issue/issueArticle used to be here too; they now come from
	// the catalog, ungated.
	custom: (rootRoute) => {
		return [
			createRoute({
				getParentRoute: () => rootRoute,
				component: LazySection,
				path: 'sections/$id',
			}),
			createRoute({
				getParentRoute: () => rootRoute,
				component: LazyGames,
				path: 'games',
			}),
			createRoute({
				getParentRoute: () => rootRoute,
				component: LazyPreview,
				path: 'preview/$id',
			}),
		];
	},
	sessionTracker: true,
	// A tenant still needs `config.app.favorite`/
	// `config.app.downloadedIssues.enabled` to see those two
	// (@shared/ui/app-menu's own gating).
	appMenu: ['favorites', 'downloadedIssues', 'install', 'about'],
});
